import type { ReactNode } from 'react';
import { useSettings } from '../state/SettingsContext';

interface BiProps {
  ar: string;
  en: string;
  /** Extra class for the primary (Arabic) line. */
  className?: string;
  /** Extra class for the secondary (English) line. */
  enClassName?: string;
  /** Wrapper element. Defaults to a <span>. */
  as?: 'span' | 'div' | 'p' | 'h1' | 'h2' | 'h3';
}

/**
 * Shows a piece of content in the chosen display mode.
 *
 *   ar      → Arabic only
 *   ar-en   → Arabic, with the ORIGINAL English underneath in smaller text
 *   en      → the ORIGINAL English only
 *
 * Both strings are always passed in; the mode only decides what is painted.
 */
export function Bi({ ar, en, className, enClassName, as = 'span' }: BiProps) {
  const { showArabic, showEnglish } = useSettings();
  const Tag = as;

  if (!showArabic) {
    return (
      <Tag className={className} dir="ltr" lang="en">
        {en}
      </Tag>
    );
  }

  return (
    <Tag className={className}>
      <span dir="rtl" lang="ar">
        {ar}
      </span>
      {showEnglish && en ? (
        <span className={enClassName ?? 'answer__en'} dir="ltr" lang="en">
          {en}
        </span>
      ) : null}
    </Tag>
  );
}

/** Renders children only in the given display modes. */
export function OnlyIf({ arabic, english, children }: { arabic?: boolean; english?: boolean; children: ReactNode }) {
  const { showArabic, showEnglish } = useSettings();
  if (arabic && !showArabic) return null;
  if (english && !showEnglish) return null;
  return <>{children}</>;
}
